// What Authorize.Net says happened, next to what SimpleCirc says happened.
//
// ARHistoryTimeline renders SC's account of each renewal attempt. This renders
// the gateway's. The cases worth looking at are where they differ: refunds (SC
// has no refund concept at all), the card as it was at charge time, an
// authorization held for merchant review that expired uncaptured, and the
// issuer's own decline reason rather than SC's paraphrase.

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ApiError,
  api,
  type CardOnFileResult,
  type GatewayFlag,
  type GatewayLedger,
  type GatewayLedgerGroup,
  type GatewayTransactionRow,
  type StoredProfile,
} from '../api/client'
import { fmtMoney } from '../lib/format'
import { Button } from './Button'
import { PubBadge } from './PubBadge'

const FLAG_LABEL: Record<GatewayFlag, string> = {
  held_and_expired: 'Held for review, then expired',
  lost_or_stolen: 'Card reported lost or stolen',
  card_swapped: 'Card replaced mid-history',
  refunded: 'Refunded',
  cvv_mismatch: 'CVV mismatch',
}

const FLAG_TONE: Record<GatewayFlag, string> = {
  held_and_expired: 'border-radar-red/60 text-radar-red',
  lost_or_stolen: 'border-radar-red/60 text-radar-red',
  card_swapped: 'border-radar-cyan/60 text-radar-cyan',
  refunded: 'border-amber-glow/60 text-amber-glow',
  cvv_mismatch: 'border-amber-glow/60 text-amber-glow',
}

// What each flag means for the person reading it. The distinctions decide
// whether contacting this member is useful or pointless.
const FLAG_HINT: Record<GatewayFlag, string> = {
  held_and_expired:
    'The gateway accepted the charge but held it for merchant review. Nobody reviewed it, the authorization expired, and the money was never captured.',
  lost_or_stolen:
    'The issuer reported the card lost or stolen. The member already has a replacement — asking for it will work.',
  card_swapped:
    'Charges landed on more than one card number. An "invalid card" failure followed by a success on a different last-4 is a replacement, not a dead card.',
  refunded: 'The gateway returned money on this subscription.',
  cvv_mismatch: 'Declined on the card security code, not the card itself.',
}

const STATUS_TONE: Record<string, string> = {
  settledSuccessfully: 'text-radar-green',
  refundSettledSuccessfully: 'text-radar-red',
  declined: 'text-amber-glow',
  generalError: 'text-amber-glow',
  expired: 'text-radar-red',
  voided: 'text-paper-trace',
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function fmtDay(iso: string | null): string {
  if (!iso) return 'an unknown date'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** checking -> "Checking", savings -> "Savings", businessChecking -> "Business
 *  checking" — used everywhere a bank (eCheck) instrument is shown so a
 *  bank profile never falls into a card's expiry-masked rendering. */
function bankLabel(accountType: string): string {
  return accountType === 'businessChecking' ? 'Business checking' : capitalize(accountType)
}

function fmtWindow(iso: string | null): string {
  if (!iso) return 'the ledger window'
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

export function GatewayLedgerPanel({ subscriberId }: { subscriberId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['gateway-ledger', subscriberId],
    queryFn: () => api.get<GatewayLedger>(`/api/subscribers/${subscriberId}/gateway-ledger`),
    enabled: !!subscriberId,
  })

  if (isLoading) return <div className="label text-paper-trace p-4">Loading gateway ledger…</div>
  if (!data) return null

  if (data.groups.length === 0) {
    return (
      <div className="label text-paper-trace p-4 border border-ink-500 bg-ink-800/40">
        No subscriptions to check against the gateway.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="text-[11px] text-paper-trace font-mono">
        Authorize.Net settlements mirrored since {fmtWindow(data.ledger_window_start)}.
        Refunds, voids and held authorizations appear here and nowhere else —
        SimpleCirc's renewal export has no concept of them.
      </div>

      {data.groups.map((g) => (
        <GatewayGroup
          key={g.subscription_id}
          subscriberId={subscriberId}
          group={g}
          windowStart={data.ledger_window_start}
          cardCheckAvailable={data.card_check_available}
        />
      ))}
    </div>
  )
}

function GatewayGroup({
  subscriberId, group, windowStart, cardCheckAvailable,
}: {
  subscriberId: number
  group: GatewayLedgerGroup
  windowStart: string | null
  cardCheckAvailable: boolean
}) {
  const { summary } = group

  return (
    <div className="border border-ink-500 bg-ink-800/40">
      <header className="flex items-center justify-between gap-3 flex-wrap px-4 py-2 border-b border-ink-500/60">
        <div className="flex items-center gap-3 flex-wrap">
          <PubBadge slug={group.publication_slug} />
          {/* Subscription-level identifiers. They live here rather than on each
              row because that is what they are: the profile belongs to the
              member, while merchantCustomerId is recorded per charge and drifts.
              Many charges carry no profile at all, so the row-level line below
              would otherwise be the only place to look and would be empty.

              Each is named for what it actually is. `profile` is the only
              Authorize.Net-ASSIGNED customer identifier and the one to type
              into the merchant interface. merchantCustomerId is by definition
              the MERCHANT's own id, so the other two are both SimpleCirc
              numbers: the account id we stored, and the subscription id the
              gateway currently reports against the profile. Calling either of
              them "customer" read as an Authorize.Net id and sent people to
              look up a number Authorize.Net has never heard of. Showing both
              also makes a stale stored key visible: when they disagree, ours
              is the one that has moved. */}
          <span className="font-mono text-[10px] text-paper-trace flex gap-x-3 flex-wrap">
            {/* The swept reading wins when there is one — it is what the
                gateway said most recently. Falling back to the subscription's
                own ids is what puts a purchase made here on the screen: it
                holds both from the moment it is charged, and the nightly
                sweep can never reach it (it selects profiles the settled
                ledger names, and an inline-vaulted nonce charge names none). */}
            {(group.stored_profile || group.gateway_profile_id) && (
              <>
                <span>
                  profile{' '}
                  {group.stored_profile?.customer_profile_id ?? group.gateway_profile_id}
                </span>
                <span>
                  payment{' '}
                  {group.stored_profile?.payment_profile_id
                    ?? group.gateway_payment_profile_id
                    ?? '—'}
                </span>
              </>
            )}
            {group.gateway_customer_id && (
              // A purchase made here stores the `aam-…` merchantCustomerId we
              // minted, not a SimpleCirc account number, so the SimpleCirc
              // label would name the wrong system entirely.
              <span>
                {group.gateway_customer_id.startsWith('aam-') ? 'gateway mcid' : 'simplecirc acct'}
                {' '}{group.gateway_customer_id}
              </span>
            )}
            {group.stored_profile?.merchant_customer_id && (
              <span>gateway mcid {group.stored_profile.merchant_customer_id}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StoredCard profile={group.stored_profile} />
          <CardCheck
            subscriberId={subscriberId}
            subscriptionId={group.subscription_id}
            available={cardCheckAvailable && !!group.gateway_customer_id}
          />
        </div>
      </header>

      {group.coverage === 'covered' ? (
        <>
          <div className="px-4 py-3 flex items-baseline gap-6 flex-wrap border-b border-ink-500/30">
            <Figure label="Gross" value={fmtMoney(summary.gross, false, 2)} tone="text-paper" />
            <Figure
              label="Refunded"
              value={summary.refunded > 0 ? `−${fmtMoney(summary.refunded, false, 2)}` : '—'}
              tone={summary.refunded > 0 ? 'text-radar-red' : 'text-paper-trace'}
            />
            <Figure label="Net" value={fmtMoney(summary.net, false, 2)} tone="text-radar-green" />
            {summary.uncaptured > 0 && (
              <Figure
                label="Never captured"
                value={fmtMoney(summary.uncaptured, false, 2)}
                tone="text-radar-red"
              />
            )}
          </div>

          {summary.flags.length > 0 && (
            <div className="px-4 py-2 flex gap-2 flex-wrap border-b border-ink-500/30">
              {summary.flags.map((f) => (
                <span
                  key={f}
                  title={FLAG_HINT[f]}
                  className={`font-mono text-[10px] uppercase tracking-label border px-2 py-0.5 ${FLAG_TONE[f]}`}
                >
                  {FLAG_LABEL[f]}
                </span>
              ))}
            </div>
          )}

          {group.transactions.map((t) => <TxnRow key={t.trans_id} t={t} />)}
        </>
      ) : (
        <EmptyState group={group} windowStart={windowStart} />
      )}
    </div>
  )
}

/** The two empty states are different facts and must read differently. One is a
 *  quiet subscriber; the other is a hole in our data. */
function EmptyState({
  group, windowStart,
}: { group: GatewayLedgerGroup; windowStart: string | null }) {
  if (group.coverage === 'no_transactions') {
    return (
      <div className="px-4 py-3 text-[12px] text-paper-trace font-mono">
        No gateway activity since {fmtWindow(windowStart)}.
      </div>
    )
  }
  return (
    <div className="px-4 py-3 text-[12px] font-mono space-y-1">
      <div className="text-amber-glow">No gateway identity confirmed.</div>
      <div className="text-paper-trace">
        Nothing on this subscription can be matched to Authorize.Net, so an
        empty ledger here is not evidence the member never paid.
        {group.stored_transaction_id && (
          <> Last import stored transaction {group.stored_transaction_id}, which a live lookup could resolve.</>
        )}
      </div>
    </div>
  )
}

function TxnRow({ t }: { t: GatewayTransactionRow }) {
  const isRefund = (t.net_amount ?? 0) < 0
  const amount = t.net_amount ?? t.auth_amount ?? 0
  return (
    <div className="px-4 py-3 border-b border-ink-500/30 last:border-0">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="font-mono text-[12px] text-paper-dim">{fmtTime(t.submitted_at)}</div>
        <div className={`font-mono text-[12px] ${STATUS_TONE[t.transaction_status ?? ''] ?? 'text-paper'}`}>
          {t.transaction_status ?? '—'}
        </div>
        <div className={`font-mono text-[12px] tabular-nums ${isRefund ? 'text-radar-red' : 'text-paper'}`}>
          {isRefund ? '−' : ''}{fmtMoney(Math.abs(amount), false, 2)}
        </div>
      </div>
      <div className="text-[11px] text-paper-trace font-mono mt-1">
        {t.card_type ?? 'card'} ····{t.last4 ?? '????'} · txn {t.trans_id}
      </div>
      {/* What THIS charge carried, shown only where it carried something.
          Roughly a fifth of transaction details have no profile element at all,
          and a row of em-dashes reads as missing data rather than as the
          gateway's own record. The subscription's identifiers are in the header. */}
      {(t.customer_profile_id || t.payment_profile_id || t.merchant_customer_id) && (
        <div className="text-[10px] text-paper-trace/70 font-mono mt-0.5 flex gap-x-4 flex-wrap">
          {t.customer_profile_id && <span>profile {t.customer_profile_id}</span>}
          {t.payment_profile_id && <span>payment {t.payment_profile_id}</span>}
          {/* mcid, not "customer": this is the merchant's id, which for us
              is a SimpleCirc number, never an Authorize.Net one. */}
          {t.merchant_customer_id && <span>mcid {t.merchant_customer_id}</span>}
        </div>
      )}
      {t.response_reason_description && t.response_reason_code !== 1 && (
        <div className="text-[11px] text-paper-dim mt-1 italic">
          gateway {t.response_reason_code}: "{t.response_reason_description}"
        </div>
      )}
    </div>
  )
}

/** The nightly sweep's reading — shown without a gateway call. The button
 *  beside it re-reads live when someone needs this second, not last night. */
function StoredCard({ profile }: { profile: StoredProfile | null }) {
  if (!profile) return null

  if (profile.missing_at) {
    return (
      <span className="font-mono text-[11px] text-radar-red"
            title={`The gateway returned E00040 for this profile when swept on ${fmtDay(profile.missing_at)}.`}>
        profile gone at the gateway
      </span>
    )
  }

  // A bank (eCheck) profile: check on this, never by inferring it from a
  // missing expiry — a bank account has nothing to expire, which is not the
  // same fact as a card whose details never got read.
  if (profile.account_type) {
    return (
      <span className="font-mono text-[11px]" title={`Swept ${fmtDay(profile.checked_at)}.`}>
        <span className="text-paper">
          {bankLabel(profile.account_type)}{' '}····{profile.last4 ?? '????'}
        </span>
      </span>
    )
  }

  // Profile present, card absent: the Card Account Updater deletion. Must not
  // read as "no data" — it is the one state no retry can fix.
  if (!profile.last4) {
    return (
      <span className="font-mono text-[11px] text-radar-red"
            title="The profile exists but holds no card. Card Account Updater removed it after the issuer reported the account closed with no replacement — the member must supply a new one.">
        no card on profile — CAU removed it
      </span>
    )
  }

  return (
    <span className="font-mono text-[11px]" title={`Swept ${fmtDay(profile.checked_at)}. Click to re-read live.`}>
      <span className="text-paper">{profile.card_type} ····{profile.last4}</span>
      {profile.expiry ? (
        <span className={profile.expires_before_renewal ? 'text-amber-glow' : 'text-paper-trace'}>
          {' '}· exp {profile.expiry}
          {profile.expires_before_renewal && ' — before renewal'}
        </span>
      ) : (
        <span className="text-paper-trace"> · expiry masked</span>
      )}
    </span>
  )
}

/** One live getCustomerProfile call, on click. Never on page load — a page view
 *  should cost the gateway nothing. */
function CardCheck({
  subscriberId, subscriptionId, available,
}: { subscriberId: number; subscriptionId: number; available: boolean }) {
  const check = useMutation({
    mutationFn: () => api.get<CardOnFileResult>(
      `/api/subscribers/${subscriberId}/subscriptions/${subscriptionId}/card-on-file`,
    ),
  })

  if (!available) {
    return (
      <span className="font-mono text-[10px] text-paper-trace">
        card check unavailable — no confirmed gateway key
      </span>
    )
  }

  return (
    <div className="flex items-center gap-3">
      {check.data && <CardResult r={check.data} />}
      {check.error instanceof ApiError && (
        <span className="font-mono text-[10px] text-radar-red max-w-[340px] text-right">
          {String((check.error.detail as { detail?: string })?.detail ?? check.error.message)}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={check.isPending}
        onClick={() => check.mutate()}
      >
        {check.isPending ? 'Asking gateway…' : 'Check card on file'}
      </Button>
    </div>
  )
}

function CardResult({ r }: { r: CardOnFileResult }) {
  if (r.state === 'no_profile') {
    // Absence of a card is never claimed here. An earlier version said "no
    // saved card" in red on this branch and was wrong for 14 of 30 sampled
    // subscriptions — each held a live card under a merchantCustomerId we had
    // never recorded. A profile we watched disappear is a different matter.
    if (r.reason === 'profile_deleted') {
      return (
        <span
          className="font-mono text-[11px] text-radar-red"
          title="We hold a customerProfileId the gateway once honoured and no longer recognises. The profile was removed outright, not just emptied of its card."
        >
          profile deleted at the gateway
        </span>
      )
    }
    // Our key matches nothing the gateway ever told us about this subscription,
    // so its E00040 proves nothing. Claiming "no card" here would dun members
    // whose cards are fine.
    return (
      <span
        className="font-mono text-[11px] text-paper-trace"
        title={`Tried: ${r.identifiers_tried.join(', ') || 'nothing'}. None resolved. This is not evidence the member has no card — a profile may exist under an identifier we never recorded, which is common enough that we refuse to infer absence from it.`}
      >
        no card found under our {r.identifiers_tried.length === 1 ? 'identifier' : `${r.identifiers_tried.length} identifiers`} — not proof of none
      </span>
    )
  }
  if (r.state === 'no_card_on_file') {
    return (
      <span
        className="font-mono text-[11px] text-radar-red"
        title="Card Account Updater asked the issuer, was told the account is closed with no replacement, and deleted the stored card. No retry can fix this — the member must supply a new card."
      >
        no card on file — deleted by Card Account Updater
      </span>
    )
  }
  // A bank (eCheck) profile: check on this, never by inferring it from a
  // missing expiry. Rendering the branch below on a bank profile would say
  // "expiry masked by gateway" — a claim that a real expiry exists and was
  // withheld, which is false for an instrument with nothing to expire.
  if (r.account_type) {
    return (
      <span className="font-mono text-[11px]">
        <span className="text-paper">{bankLabel(r.account_type)} ····{r.last4}</span>
      </span>
    )
  }
  return (
    <span className="font-mono text-[11px]">
      <span className="text-paper">{r.card_type} ····{r.last4}</span>
      {r.expiry ? (
        <span className={r.expires_before_renewal ? 'text-amber-glow' : 'text-paper-trace'}>
          {' '}· exp {r.expiry}
          {r.expires_before_renewal && ' — expires before renewal'}
        </span>
      ) : (
        <span className="text-paper-trace"> · expiry masked by gateway</span>
      )}
    </span>
  )
}

function Figure({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div>
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[16px] tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}
